/**
 * Русские сообщения об ошибках API для тостов и инлайн-ошибок.
 *
 * Бэкенд отдаёт `{ statusCode, message, code, ...доп. поля }`. `message` — текст для
 * разработчика (часто английский, может меняться), его НЕЛЬЗЯ показывать админу.
 * Ветвимся только по `code`.
 *
 * ErrorCode — зеркало backend/src/common/errors/error-codes.ts (коды только
 * добавляются, не переименовываются). При добавлении кода на бэкенде допишите его
 * сюда и в ERROR_MESSAGES.
 */
export const ErrorCode = {
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL: 'INTERNAL',
  UPSTREAM_UNAVAILABLE: 'UPSTREAM_UNAVAILABLE',

  AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  AUTH_REFRESH_INVALID: 'AUTH_REFRESH_INVALID',
  AUTH_SUSPICIOUS_ACTIVITY: 'AUTH_SUSPICIOUS_ACTIVITY',
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_PASSWORD_NOT_SET: 'AUTH_PASSWORD_NOT_SET',
  AUTH_INVALID_OTP: 'AUTH_INVALID_OTP',
  AUTH_OTP_EXPIRED: 'AUTH_OTP_EXPIRED',
  AUTH_OAUTH_INVALID: 'AUTH_OAUTH_INVALID',
  AUTH_ACCOUNT_BLOCKED: 'AUTH_ACCOUNT_BLOCKED',
  AUTH_ACCOUNT_DELETED: 'AUTH_ACCOUNT_DELETED',
  AUTH_USER_NOT_FOUND: 'AUTH_USER_NOT_FOUND',
  AUTH_EMAIL_TAKEN: 'AUTH_EMAIL_TAKEN',
  AUTH_USERNAME_TAKEN: 'AUTH_USERNAME_TAKEN',
  AUTH_USERNAME_TOO_SHORT: 'AUTH_USERNAME_TOO_SHORT',
  AUTH_NOT_ANONYMOUS: 'AUTH_NOT_ANONYMOUS',
  AUTH_ADMIN_REQUIRED: 'AUTH_ADMIN_REQUIRED',

  PREMIUM_REQUIRED: 'PREMIUM_REQUIRED',
  LIMIT_REACHED: 'LIMIT_REACHED',

  PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
  PLAN_NOT_AVAILABLE: 'PLAN_NOT_AVAILABLE',
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  ORDER_NOT_PENDING: 'ORDER_NOT_PENDING',
  PAYMENT_NOT_FOUND: 'PAYMENT_NOT_FOUND',
  PAYMENT_NOT_PENDING: 'PAYMENT_NOT_PENDING',
  PAYMENT_OTP_EXPIRED: 'PAYMENT_OTP_EXPIRED',
  PAYMENT_DECLINED: 'PAYMENT_DECLINED',
  PAYMENT_PROVIDER_UNSUPPORTED: 'PAYMENT_PROVIDER_UNSUPPORTED',
  PROMO_NOT_FOUND: 'PROMO_NOT_FOUND',
  PROMO_NOT_YET_VALID: 'PROMO_NOT_YET_VALID',
  PROMO_EXPIRED: 'PROMO_EXPIRED',
  PROMO_EXHAUSTED: 'PROMO_EXHAUSTED',

  AI_UNAVAILABLE: 'AI_UNAVAILABLE',

  GAME_SESSION_NOT_FOUND: 'GAME_SESSION_NOT_FOUND',
  GAME_SESSION_FORBIDDEN: 'GAME_SESSION_FORBIDDEN',
  GAME_SESSION_NOT_ACTIVE: 'GAME_SESSION_NOT_ACTIVE',
  GAME_SESSION_EXPIRED: 'GAME_SESSION_EXPIRED',
  GAME_INVALID_SUBMISSION: 'GAME_INVALID_SUBMISSION',
  DUEL_MATCH_NOT_FOUND: 'DUEL_MATCH_NOT_FOUND',
  DUEL_NOT_PARTICIPANT: 'DUEL_NOT_PARTICIPANT',
  DUEL_MATCH_CLOSED: 'DUEL_MATCH_CLOSED',
  DUEL_ALREADY_SUBMITTED: 'DUEL_ALREADY_SUBMITTED',
  DAILY_ALREADY_ANSWERED: 'DAILY_ALREADY_ANSWERED',
  DAILY_OUT_OF_WINDOW: 'DAILY_OUT_OF_WINDOW',
  DAILY_FREEZE_UNAVAILABLE: 'DAILY_FREEZE_UNAVAILABLE',

  SAME_USER: 'SAME_USER',
  DUPLICATE_DELETED: 'DUPLICATE_DELETED',
  MAIN_DELETED: 'MAIN_DELETED',
  DUPLICATE_MERGED: 'DUPLICATE_MERGED',
  MAIN_MERGED: 'MAIN_MERGED',
  DUPLICATE_NO_TELEGRAM: 'DUPLICATE_NO_TELEGRAM',
  MAIN_HAS_OTHER_TELEGRAM: 'MAIN_HAS_OTHER_TELEGRAM',
  DUPLICATE_HAS_PAYMENTS: 'DUPLICATE_HAS_PAYMENTS',
  ALREADY_MERGED: 'ALREADY_MERGED',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

/** Общее сообщение, если ничего точнее сказать нельзя. */
export const GENERIC_ERROR_MESSAGE = 'Что-то пошло не так. Попробуйте ещё раз.';

/**
 * Причина ошибки по коду. Общие коды (BAD_REQUEST, NOT_FOUND, CONFLICT…) не несут
 * смысла сами по себе — для них дополняем контекстный текст вызывающего кода
 * («Не удалось сохранить тариф: …»).
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  BAD_REQUEST: 'некорректный запрос',
  VALIDATION_FAILED: 'проверьте заполнение полей',
  UNAUTHORIZED: 'сессия истекла, войдите снова',
  FORBIDDEN: 'недостаточно прав',
  NOT_FOUND: 'запись не найдена (возможно, её уже удалили)',
  CONFLICT: 'данные изменились или такая запись уже существует — обновите страницу',
  RATE_LIMITED: 'слишком много запросов, подождите немного',
  INTERNAL: 'ошибка на сервере',
  UPSTREAM_UNAVAILABLE: 'внешний сервис недоступен, попробуйте позже',

  AUTH_TOKEN_MISSING: 'сессия истекла, войдите снова',
  AUTH_TOKEN_INVALID: 'сессия истекла, войдите снова',
  AUTH_REFRESH_INVALID: 'сессия истекла, войдите снова',
  AUTH_SUSPICIOUS_ACTIVITY: 'сессия отозвана из-за подозрительной активности, войдите снова',
  AUTH_INVALID_CREDENTIALS: 'неверный логин или пароль',
  AUTH_PASSWORD_NOT_SET: 'у аккаунта не задан пароль',
  AUTH_INVALID_OTP: 'неверный код',
  AUTH_OTP_EXPIRED: 'код истёк, запросите новый',
  AUTH_OAUTH_INVALID: 'вход через Google/Apple не подтверждён',
  AUTH_ACCOUNT_BLOCKED: 'аккаунт заблокирован',
  AUTH_ACCOUNT_DELETED: 'аккаунт удалён',
  AUTH_USER_NOT_FOUND: 'пользователь не найден',
  AUTH_EMAIL_TAKEN: 'этот email уже занят',
  AUTH_USERNAME_TAKEN: 'этот логин уже занят',
  AUTH_USERNAME_TOO_SHORT: 'логин должен быть не короче 6 символов',
  AUTH_NOT_ANONYMOUS: 'операция доступна только анонимному пользователю',
  AUTH_ADMIN_REQUIRED: 'нужна роль администратора',

  PREMIUM_REQUIRED: 'функция доступна только с Premium',
  LIMIT_REACHED: 'исчерпан дневной лимит',

  PLAN_NOT_FOUND: 'тариф не найден или неактивен',
  PLAN_NOT_AVAILABLE: 'тариф недоступен этому пользователю',
  ORDER_NOT_FOUND: 'заказ не найден',
  ORDER_NOT_PENDING: 'заказ уже оплачен или отменён',
  PAYMENT_NOT_FOUND: 'платёж не найден',
  PAYMENT_NOT_PENDING: 'платёж уже проведён или истёк',
  PAYMENT_OTP_EXPIRED: 'код подтверждения оплаты истёк',
  PAYMENT_DECLINED: 'платёжный шлюз отклонил операцию',
  PAYMENT_PROVIDER_UNSUPPORTED: 'провайдер оплаты не поддерживается',
  PROMO_NOT_FOUND: 'промокод не найден или выключен',
  PROMO_NOT_YET_VALID: 'промокод ещё не действует',
  PROMO_EXPIRED: 'срок промокода истёк',
  PROMO_EXHAUSTED: 'лимит использований промокода исчерпан',

  AI_UNAVAILABLE: 'AI-сервис недоступен, попробуйте позже',

  GAME_SESSION_NOT_FOUND: 'игровая сессия не найдена',
  GAME_SESSION_FORBIDDEN: 'сессия принадлежит другому пользователю',
  GAME_SESSION_NOT_ACTIVE: 'игровая сессия уже завершена',
  GAME_SESSION_EXPIRED: 'время игровой сессии истекло',
  GAME_INVALID_SUBMISSION: 'некорректные данные забега',
  DUEL_MATCH_NOT_FOUND: 'матч дуэли не найден',
  DUEL_NOT_PARTICIPANT: 'пользователь не участник матча',
  DUEL_MATCH_CLOSED: 'матч уже закрыт',
  DUEL_ALREADY_SUBMITTED: 'ответы в матч уже отправлены',
  DAILY_ALREADY_ANSWERED: 'на вопрос дня уже ответили',
  DAILY_OUT_OF_WINDOW: 'дата вне окна доигрывания',
  DAILY_FREEZE_UNAVAILABLE: 'заморозка стрика недоступна',

  SAME_USER: 'нельзя склеить пользователя с самим собой',
  DUPLICATE_DELETED: 'дубль удалён',
  MAIN_DELETED: 'основной аккаунт удалён',
  DUPLICATE_MERGED: 'дубль уже склеен',
  MAIN_MERGED: 'основной аккаунт сам склеен в другой',
  DUPLICATE_NO_TELEGRAM: 'у дубля нет Telegram',
  MAIN_HAS_OTHER_TELEGRAM: 'у основного аккаунта уже другой Telegram',
  DUPLICATE_HAS_PAYMENTS:
    'у дубля есть оплаты или подписки — склейка запрещена. Решите вручную: возврат или продление на основном аккаунте',
  ALREADY_MERGED: 'эта пара уже склеена',
};

/** Минимальная форма ошибки axios — без зависимости от типов axios. */
interface ApiErrorLike {
  response?: { status?: number; data?: unknown };
  request?: unknown;
  code?: string;
}

interface ApiErrorBody {
  code?: unknown;
  fields?: unknown;
  message?: unknown;
}

function isKnownCode(code: unknown): code is ErrorCode {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, code);
}

/** `code` из тела ошибки API (или undefined). Старые ответы клали его в `message.code`. */
export function getErrorCode(error: unknown): string | undefined {
  const data = (error as ApiErrorLike | null)?.response?.data as ApiErrorBody | undefined;
  if (!data || typeof data !== 'object') return undefined;
  if (typeof data.code === 'string') return data.code;
  const nested = data.message as ApiErrorBody | undefined;
  if (nested && typeof nested === 'object' && typeof nested.code === 'string') return nested.code;
  return undefined;
}

/** Пути полей из VALIDATION_FAILED (`fields: string[]`). */
export function getErrorFields(error: unknown): string[] {
  const data = (error as ApiErrorLike | null)?.response?.data as ApiErrorBody | undefined;
  const fields = data?.fields;
  return Array.isArray(fields) ? fields.filter((f): f is string => typeof f === 'string') : [];
}

/** Русская причина ошибки без контекста или null, если сказать нечего. */
export function getErrorReason(error: unknown): string | null {
  const e = error as ApiErrorLike | null;
  if (!e) return null;

  if (!e.response) {
    // Запрос ушёл, но ответа нет: сеть, CORS, таймаут, сервер лежит.
    if (e.request || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') {
      return 'нет связи с сервером, проверьте подключение';
    }
    return null;
  }

  const code = getErrorCode(error);
  if (code === ErrorCode.VALIDATION_FAILED) {
    const fields = getErrorFields(error);
    return fields.length
      ? `проверьте поля: ${fields.join(', ')}`
      : ERROR_MESSAGES.VALIDATION_FAILED;
  }
  if (isKnownCode(code)) return ERROR_MESSAGES[code];

  // Код не пришёл или незнакомый — судим по HTTP-статусу.
  const status = e.response.status ?? 0;
  if (status === 401) return ERROR_MESSAGES.UNAUTHORIZED;
  if (status === 403) return ERROR_MESSAGES.FORBIDDEN;
  if (status === 404) return ERROR_MESSAGES.NOT_FOUND;
  if (status === 409) return ERROR_MESSAGES.CONFLICT;
  if (status === 429) return ERROR_MESSAGES.RATE_LIMITED;
  if (status >= 500) return ERROR_MESSAGES.INTERNAL;
  return null;
}

/**
 * Текст ошибки для админа: «<контекст>: <причина>». Сырой текст сервера не
 * показывается никогда. `fallback` — что именно не получилось («Не удалось сохранить тариф»).
 */
export function getErrorMessage(error: unknown, fallback: string = GENERIC_ERROR_MESSAGE): string {
  const reason = getErrorReason(error);
  if (!reason) return fallback;
  if (fallback === GENERIC_ERROR_MESSAGE) {
    return reason.charAt(0).toUpperCase() + reason.slice(1);
  }
  return `${fallback}: ${reason}`;
}
